export type OpportunityPipelineStatus = "ready" | "house_account" | "review_needed" | "needs_attention" | "archived";

export type OpportunityStatusInput = {
  archived_at?: string | null;
  account_id?: string | null;
  account_mapping_issue?: boolean;
  request_type?: string | null;
  request_status?: string | null;
  attribution_review?: string | null;
  duplicate_review?: boolean;
  stage?: string | null;
  estimated_mrr?: number | string | null;
  partner_id?: string | null;
  ownership_label?: string | null;
  ownership_expires_at?: string | null;
  last_meaningful_activity_at?: string | null;
};

type PartnerStatusInput = { id: string; status?: string | null };

function normalizeStatusValue(value?: string | null): string {
  return (value || "").trim().toLowerCase().replace(/[_-]+/g, " ");
}

export function isHouseAccountOpportunity(opportunity: Pick<OpportunityStatusInput, "attribution_review" | "ownership_label">): boolean {
  return [opportunity.attribution_review, opportunity.ownership_label]
    .map(normalizeStatusValue)
    .some((value) => ["house account", "internal", "direct", "toren owned"].includes(value));
}

export function deriveOpportunityPipelineStatus(
  opportunity: OpportunityStatusInput,
  partners: PartnerStatusInput[] = [],
): OpportunityPipelineStatus {
  if (opportunity.archived_at) return "archived";

  const houseAccount = isHouseAccountOpportunity(opportunity);
  const partner = opportunity.partner_id ? partners.find((item) => item.id === opportunity.partner_id) : undefined;
  const invalidPartner = Boolean(opportunity.partner_id && (!partner || partner.status !== "active"));
  const pendingRequest = opportunity.request_status === "pending_review";
  const conflictState = Boolean(opportunity.duplicate_review) || ["rejected_duplicate", "rejected"].includes(opportunity.attribution_review || "");
  const approvedClaimWithoutPartner = opportunity.request_type === "claim_request" && opportunity.request_status === "approved" && !opportunity.partner_id;
  const needsAttention = Boolean(opportunity.account_mapping_issue || !opportunity.account_id || invalidPartner || pendingRequest || conflictState || approvedClaimWithoutPartner);
  if (needsAttention) return "needs_attention";

  const partnerOwned = !houseAccount && Boolean(opportunity.partner_id || opportunity.ownership_label?.toLowerCase().includes("partner-owned"));
  const missingEstimatedMrr = opportunity.estimated_mrr === undefined
    || opportunity.estimated_mrr === null
    || (typeof opportunity.estimated_mrr === "string" && opportunity.estimated_mrr.trim() === "");
  const reviewNeeded = opportunity.attribution_review === "pending_review"
    || ["submitted", "under_review", "closed_lost", "rejected_duplicate"].includes(opportunity.stage || "")
    || !opportunity.stage
    || missingEstimatedMrr
    || (partnerOwned && (!opportunity.last_meaningful_activity_at || !opportunity.ownership_expires_at));
  if (reviewNeeded) return "review_needed";

  return houseAccount ? "house_account" : "ready";
}

export function opportunityPipelineStatusLabel(status: OpportunityPipelineStatus): string {
  return status === "house_account" ? "House account" : status === "review_needed" ? "Review needed" : status === "needs_attention" ? "Needs attention" : status === "archived" ? "Archived" : "Ready";
}
